/**
 * Shared Zod validation schemas.
 *
 * These schemas are defined ONCE and used in two places:
 *   - the frontend, for immediate user feedback (a convenience);
 *   - the backend, as the authoritative gate (the actual control).
 *
 * The backend ALWAYS re-validates. Client-side validation is never trusted.
 */
import { z } from 'zod';
import { MAX_PAYMENT_TERMS_DAYS, MIN_PAYMENT_TERMS_DAYS, REPORT_TOKEN_LENGTH, REPORT_TOKEN_PATTERN, } from '@lims/shared-types';
import { boundedText, emailSchema, listQuerySchema, localeSchema, newPasswordSchema, PASSWORD_MAX_LENGTH, phoneSchema, reasonSchema, roleSchema, bilingualTextSchema, currencySchema, uuidSchema, } from './primitives.js';
import { clientRequiredFieldsSchema, registrationNumberSchema, } from './commercial.js';
import { reportFooterTextSchema } from './reports.js';
import { bankAccountNumberSchema, countryCodeSchema, documentWordingSchema, ibanSchema, optionalOf, swiftCodeSchema, websiteSchema, } from './organisation.js';
import { portalDefaultVisibilitySchema, portalProjectScopeSchema, } from './portal.js';
export * from './primitives.js';
export * from './organisation.js';
export * from './catalogue.js';
export * from './commercial.js';
export * from './execution.js';
export * from './finance.js';
export * from './numbering.js';
export * from './operations.js';
export * from './portal.js';
export * from './reports.js';
export * from './review.js';
export * from './samples.js';
export * from './sample-types.js';
export * from './work-orders.js';
// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
    email: emailSchema,
    // Length is bounded but complexity is not asserted here — the identity
    // provider owns password policy. This only prevents absurd payloads.
    password: z.string().min(1).max(256),
});
export const passwordResetRequestSchema = z.object({
    email: emailSchema,
});
// `newPasswordSchema` — the password policy — lives in `primitives.ts` since
// Phase 11, so the portal's reset schema can share it without importing this
// barrel; it is still exported from here through `export * from primitives`.
export const passwordResetConfirmSchema = z.object({
    token: z.string().min(1).max(512),
    password: newPasswordSchema,
});
// ---------------------------------------------------------------------------
// Users and permissions
// ---------------------------------------------------------------------------
/** The editable facts of a user, shared by creation and update. */
const userProfileFields = {
    email: emailSchema,
    displayName: boundedText(2, 200),
    role: roleSchema,
    preferredLanguage: localeSchema.optional(),
    /** Individual grants beyond the role defaults. */
    grantedPermissions: z.array(z.string().max(100)).max(200).optional(),
    /** Individual revocations despite the role defaults. */
    revokedPermissions: z.array(z.string().max(100)).max(200).optional(),
};
/**
 * Create a user.
 *
 * The laboratory manager sets the first password (decision D179): the account
 * is complete the moment it is created, and nothing is emailed. The password
 * passes through to the identity provider and is never stored, logged or
 * audited by this system.
 */
export const createUserSchema = z.object({
    ...userProfileFields,
    password: newPasswordSchema,
});
/**
 * Update a user. There is deliberately NO `password` here: a password is set
 * through its own endpoint, with its own permission and its own audit action,
 * never as a side effect of editing a name.
 */
export const updateUserSchema = z
    .object(userProfileFields)
    .partial()
    .extend({
    isActive: z.boolean().optional(),
});
/**
 * A manager sets a new password for a colleague (D180). No old password is
 * asked for — that is the point — and every session the user holds ends.
 */
export const setUserPasswordSchema = z.object({
    password: newPasswordSchema,
    reason: z.string().trim().max(2000).optional(),
});
/**
 * A signed-in person changes their OWN password (D181). The current password
 * is required, so a session left open on a shared bench computer is not
 * enough to take the account over.
 */
export const changeOwnPasswordSchema = z
    .object({
    // Bounded, not policed: it is checked against the identity provider.
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: newPasswordSchema,
})
    .refine((value) => value.newPassword !== value.currentPassword, {
    path: ['newPassword'],
    message: 'The new password must differ from the current one',
});
export const setUserPermissionsSchema = z.object({
    grantedPermissions: z.array(z.string().max(100)).max(200),
    revokedPermissions: z.array(z.string().max(100)).max(200),
});
// ---------------------------------------------------------------------------
// Tenant settings
// ---------------------------------------------------------------------------
/**
 * An OPTIONAL bilingual pair in the settings (D204). `null` clears both
 * languages, and a pair whose halves are both empty says the same thing — an
 * address the laboratory has not written is an answer, never a reason to
 * refuse the whole save. The laboratory's NAME is not one of these: it keeps
 * the at-least-one-language rule, because a laboratory with no readable name
 * in either language cannot print a single document.
 */
const clearablePair = (pair) => pair.nullable().optional();
export const updateTenantSettingsSchema = z.object({
    name: bilingualTextSchema.optional(),
    addressLine: clearablePair(documentWordingSchema(500)),
    contactEmail: emailSchema.nullable().optional(),
    contactPhone: phoneSchema.nullable().optional(),
    defaultLocale: localeSchema.optional(),
    timezone: z.string().trim().max(64).optional(),
    currency: currencySchema.optional(),
    /** VAT percentage, 0 to 100. Set to 0 to disable tax. */
    taxRatePercent: z.number().min(0).max(100).optional(),
    /** When true, an issued report is not delivered until the invoice is paid.
     *  Defaults to false so delivery is never blocked unintentionally. */
    requirePaymentBeforeDelivery: z.boolean().optional(),
    /** When true, a report may cover part of a work order without marking the
     *  whole request complete. */
    allowPartialReports: z.boolean().optional(),
    /** Show a secondary Hijri date alongside Gregorian in Arabic. */
    showHijriDates: z.boolean().optional(),
    // --- Organisation identity (Phase 4) ---
    /**
     * The laboratory's own VAT registration number and commercial registration.
     *
     * SETTINGS, not literals. They appear on quotations, invoices and reports,
     * they differ between every laboratory that buys this product, and neither
     * has ever been permitted to exist as a constant in the source
     * (CLAUDE.md section 6.1). They arrive in Phase 4 because this is the phase
     * where a laboratory first records the same two facts about its CLIENTS,
     * and it would be incoherent to ask a laboratory for a client's tax number
     * while having nowhere to put its own.
     */
    vatRegistrationNumber: registrationNumberSchema.nullable().optional(),
    commercialRegistrationNumber: registrationNumberSchema.nullable().optional(),
    // --- Client policy (Phase 4) ---
    /**
     * Which client fields THIS laboratory insists on at creation.
     *
     * Empty by default: the product requires only a name, so a reception desk
     * can always record the person standing in front of it (decision D35). A
     * laboratory with stricter intake rules opts in, and can opt back out the
     * day the rule gets in the way.
     */
    clientRequiredFields: clientRequiredFieldsSchema.optional(),
    // --- Quotation policy (Phase 5) ---
    /**
     * How long a new quotation is valid for, in days, and the standard terms
     * paragraph printed at its foot.
     *
     * SETTINGS, not literals. Thirty days and a blank terms paragraph are the
     * product's defaults for a NEW laboratory, applied in one place in the
     * backend; every laboratory edits its own and the interface never restates
     * them (CLAUDE.md section 6.1, decision D42).
     */
    quotationValidityDays: z.number().int().positive().max(3650).optional(),
    /** General conditions, copied into each new quotation (D199: up to 4,000 characters per language). */
    quotationTerms: clearablePair(documentWordingSchema(4000)),
    // --- Quotation document (D199) ---
    /**
     * The laboratory's printable identity beyond its name and address, its bank
     * details and its quotation wording. Each is this laboratory's own; none has
     * a product default (CLAUDE.md 6.1). City is wording (Arabic and English);
     * the country, website and every bank identifier are one value.
     */
    city: clearablePair(documentWordingSchema(120)),
    countryCode: optionalOf(countryCodeSchema),
    website: optionalOf(websiteSchema),
    bankAccountName: optionalOf(z.string().trim().max(200)),
    bankName: optionalOf(z.string().trim().max(200)),
    bankAccountNumber: optionalOf(bankAccountNumberSchema),
    bankIban: optionalOf(ibanSchema),
    bankSwiftCode: optionalOf(swiftCodeSchema),
    /** Copied into each new quotation, where it may be edited for that quotation. */
    quotationIntroduction: clearablePair(documentWordingSchema(2000)),
    quotationPaymentTerms: clearablePair(documentWordingSchema(1000)),
    /** Printed at the foot of every quotation page, beside the page number. */
    quotationFooterText: clearablePair(documentWordingSchema(300)),
    // --- Report settings (Phase 10) ---
    /**
     * The wording this laboratory prints at the foot of its reports, and whether
     * whoever holds a report's QR link may download the original from the public
     * verification page.
     *
     * SETTINGS, not literals, and NEITHER HAS A PRODUCT DEFAULT WORTH THE NAME
     * (`CLAUDE.md` section 6.1). The footer text is `null` in both languages
     * until a laboratory writes its own: no confidentiality note, scope
     * statement or disclaimer this product invented would belong to the
     * laboratory that printed it (D126).
     *
     * `verificationDownloadEnabled` is OFF for a new laboratory (D111). The whole
     * mechanism is built — unguessable token, controlled endpoint, a superseded
     * version resolving forward, no file for a cancelled or suspended one,
     * recorded access, rate limiting — and whether a laboratory OFFERS it is its
     * own switch. Off is the safe side of a question the laboratory has not
     * answered yet: a document that was never downloadable can be made
     * downloadable tomorrow, but a download cannot be recalled. It governs the
     * PUBLIC page only; it changes nothing about staff access, any status, or
     * the validity of any result.
     *
     * The default lives in ONE place, the backend, and the interface never
     * restates it.
     */
    reportFooterText: clearablePair(reportFooterTextSchema),
    verificationDownloadEnabled: z.boolean().optional(),
    // --- Organisation logo (Phase 3) ---
    /**
     * The stored file holding this laboratory's logo, or `null` to remove it.
     * A REFERENCE, never an image bundled with the application: no laboratory's
     * branding is ever a literal in the source (CLAUDE.md section 6.1).
     */
    logoFileId: uuidSchema.nullable().optional(),
    // --- Storage policy (Phase 3) ---
    /**
     * How long a `standard` file is kept after upload, in days. `null` means
     * "keep until deliberately deleted" and is the default — a laboratory opts
     * IN to automatic deletion, because the opposite default would quietly
     * destroy evidence.
     */
    fileRetentionDays: z.number().int().min(1).max(36500).nullable().optional(),
    /**
     * This laboratory's storage allowance in bytes. `null` falls back to the
     * deployment-wide default, so the value is not restated in two places.
     */
    storageQuotaBytes: z
        .number()
        .int()
        .positive()
        .max(1024 * 1024 * 1024 * 1024)
        .nullable()
        .optional(),
    // --- Finance and the client portal (Phase 11) ---
    /**
     * Days from issue to due date for an invoice whose draft named no due date
     * (D152); 0 means due on issue.
     *
     * The portal switch and its two defaults (D146): whether this laboratory
     * offers a client portal at all, what a contact newly enabled for it sees,
     * and which projects a new portal account covers. `none` is not a possible
     * default visibility — a contact enabled for the portal sees at least
     * reports — and financial visibility is always an explicit grant unless the
     * laboratory makes it its default.
     *
     * SETTINGS, not literals. Every default (30 days, the portal off, `reports`,
     * `all_projects`) lives in ONE place, the backend, and the interface never
     * restates it (CLAUDE.md section 6.1).
     */
    paymentTermsDays: z
        .number()
        .int()
        .min(MIN_PAYMENT_TERMS_DAYS)
        .max(MAX_PAYMENT_TERMS_DAYS)
        .optional(),
    clientPortalEnabled: z.boolean().optional(),
    portalDefaultVisibility: portalDefaultVisibilitySchema.optional(),
    portalDefaultProjectScope: portalProjectScopeSchema.optional(),
    // --- Notifications (Phase 12, D173) ---
    /** How many days ahead a calibration or maintenance date raises a
     *  notification. 0 means only once it is due. Default 30, backend only. */
    equipmentNoticeDays: z.number().int().min(0).max(365).optional(),
});
// ---------------------------------------------------------------------------
// QR verification
// ---------------------------------------------------------------------------
/**
 * Public verification token.
 *
 * 32 cryptographically random bytes, base64url-encoded: 43 characters, no
 * padding (D122). It is never a sequential id, a storage path or a file name.
 *
 * CORRECTED IN PHASE 10, and deliberately: this schema was written in Phase 0
 * as a 32-character base32 placeholder, before any token was issued. Phase 10
 * issues the real ones, and a shared schema that refused every token the
 * product mints would be worse than none. The shape is taken from
 * `REPORT_TOKEN_PATTERN` rather than restated, so the two can never drift.
 *
 * Matching the shape before any database lookup is what lets a malformed token
 * be refused with the same `404` as an unknown one, without a query: it blunts
 * enumeration and keeps abuse off the database.
 */
export const verificationTokenSchema = z
    .string()
    .trim()
    .length(REPORT_TOKEN_LENGTH)
    .regex(REPORT_TOKEN_PATTERN, 'Invalid verification token');
// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------
/**
 * Retention policies a stored file may carry.
 *
 * `permanent` exists because some files must survive every cleanup rule the
 * laboratory can configure — an issued report is immutable evidence, not a
 * working document, and no retention setting may quietly remove one.
 */
export const RETENTION_POLICIES = ['standard', 'permanent'];
export const retentionPolicySchema = z.enum(RETENTION_POLICIES);
/**
 * The entity a file belongs to.
 *
 * Free-form within bounds rather than an enum, because later phases attach
 * files to records this phase has not built yet. It is metadata: it never
 * takes part in building a storage path.
 */
export const fileOwnerEntityTypeSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'Entity type must be lowercase letters, digits and underscores');
export const fileUploadMetadataSchema = z.object({
    /** Original filename, retained as metadata only. It is NEVER used to build a
     *  storage path — stored names are server-generated and opaque. */
    originalName: boundedText(1, 255),
    entityType: fileOwnerEntityTypeSchema,
    /** Optional: a file may be uploaded before the record it will belong to
     *  exists — a logo, for instance, is chosen before it is saved. */
    entityId: uuidSchema.optional(),
    description: z.string().trim().max(1000).optional(),
    retentionPolicy: retentionPolicySchema.optional(),
});
/** Filters for listing a laboratory's own files. */
export const listFilesQuerySchema = listQuerySchema.extend({
    entityType: fileOwnerEntityTypeSchema.optional(),
    entityId: uuidSchema.optional(),
    /** Deleted files are hidden by default; their metadata rows never leave. */
    includeDeleted: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
/** Deleting a file is soft and audited, and asks why. */
export const deleteFileSchema = z.object({
    reason: reasonSchema.optional(),
});
export { z };
//# sourceMappingURL=index.js.map